'use client';
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Eye, X } from "lucide-react";
import { useT } from "../../i18n";
import { SectionKicker } from "./shared";

export function ActOne({ onComplete }: { onComplete: () => void }) {
  const t = useT();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [seen, setSeen] = useState<number[]>([]);

  const frictionPoints = useMemo(() => [
    {
      number: "01",
      eyebrow: t('landlink.one.f1.eyebrow'),
      title: t('landlink.one.f1.title'),
      text: t('landlink.one.f1.text'),
    },
    {
      number: "02",
      eyebrow: t('landlink.one.f2.eyebrow'),
      title: t('landlink.one.f2.title'),
      text: t('landlink.one.f2.text'),
    },
    {
      number: "03",
      eyebrow: t('landlink.one.f3.eyebrow'),
      title: t('landlink.one.f3.title'),
      text: t('landlink.one.f3.text'),
    },
  ], [t]);

  useEffect(() => {
    if (seen.length === frictionPoints.length && activeIndex === null) {
      const timer = window.setTimeout(onComplete, 1200);
      return () => window.clearTimeout(timer);
    }
  }, [seen, activeIndex, onComplete, frictionPoints.length]);

  const open = (index: number) => {
    setActiveIndex(index);
    setSeen((current) => (current.includes(index) ? current : [...current, index]));
  };

  const next = () => {
    if (activeIndex === null) return;
    const nextIndex = (activeIndex + 1) % frictionPoints.length;
    open(nextIndex);
  };

  return (
    <motion.section
      className="act act-one"
      initial={{ opacity: 0, x: "12%" }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: "-10%", filter: "blur(8px)" }}
      transition={{ duration: 0.6 }}
    >
      <div className="act-bg" />
      <div className="shade" />
      <div className="grain" />

      <div className="act-content">
        <div className="friction-copy">
          <SectionKicker>{t('landlink.one.kicker')}</SectionKicker>
          <h2>{t('landlink.one.title')}</h2>
          <p className="body-text">{t('landlink.one.body')}</p>
        </div>

        <div className="friction-stage">
          {frictionPoints.map((point, index) => {
            const visited = seen.includes(index);
            return (
              <motion.button
                key={point.number}
                className={`signal signal-${index + 1} ${visited ? "seen" : ""}`}
                onClick={() => open(index)}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35 + index * 0.15, type: "spring" }}
                whileTap={{ scale: 0.96 }}
                aria-label={point.title}
              >
                <span className="signal-ring" />
                <span className="signal-core">
                  <Eye size={22} />
                </span>
                <b>
                  <em>{point.number}.</em> {point.title}
                </b>
              </motion.button>
            );
          })}

          <AnimatePresence>
            {activeIndex !== null && (
              <motion.div
                className="insight"
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.25 }}
              >
                <button
                  className="insight-close"
                  onClick={() => setActiveIndex(null)}
                  aria-label={t('landlink.one.closeAria')}
                >
                  <X size={20} />
                </button>
                <small>{frictionPoints[activeIndex].eyebrow}</small>
                <div className="insight-title">
                  <strong>{frictionPoints[activeIndex].number}</strong>
                  <h3>{frictionPoints[activeIndex].title}</h3>
                </div>
                <p>{frictionPoints[activeIndex].text}</p>
                <button className="insight-continue" onClick={next}>
                  {seen.length === frictionPoints.length ? t('landlink.one.continueDone') : t('landlink.one.continueNext')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="friction-progress">
          <span>{t('landlink.one.progressPre')} {seen.length} / 3 {t('landlink.one.progressPost')}</span>
          <div>
            {frictionPoints.map((point, index) => (
              <i key={point.number} className={seen.includes(index) ? "on" : ""} />
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
